import { forwardRef } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

// @mui
import { Box, Breadcrumbs, Card, CardContent, CardHeader, Container, Link, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { SimBContainer } from '../container';
import { PageBreadcrumbs } from '.';
import MOCK_ACCOUNT from '../../_mock/account.mock';



// ================================|| SUB PAGE CONTAINER ||=============================== //

const SimSubPageContainer = styled(Box)(
  ({ theme }) => `
     padding-bottom: ${theme.spacing(12)};
`
);
const SubPageContainer = ({ children }) => {
  const theme = useTheme();
  return (
    <SimSubPageContainer className="Sim-sub-page-container">
      {children}
    </SimSubPageContainer>
  );
};
SubPageContainer.propTypes = {
  children: PropTypes.node.isRequired
};

// =================================|| SUB PAGE HEADER ||================================= //

const SimSubPageHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgImage',
})(({ bgImage, theme }) => ({
  position: 'relative',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundOrigin: 'border-box',
  backgroundColor: theme.palette.background.default,
  backgroundImage: `url('${bgImage}')`,
  paddingTop: theme.spacing(23),
  paddingBottom: theme.spacing(11),
  [theme.breakpoints.down('lg')]: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
  },
  '&:after': {
    content: '""',
    width: '100%',
    height: '100%',
    position: 'absolute',
    left: '50%',
    bottom: 0,
    transform: 'translate(-50% , 0)',
    zIndex: 0,
    backgroundImage: theme.palette.gradients.blue1,
  }
}));
const SimSubPageHBanner = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgImage',
})(({ bgImage, theme }) => `
background-position: center;
background-size: cover;
position: relative;
z-index: 1;
`);
const SubPageHeader = ({ children, bgImage, title, subtitle }) => {
  const theme = useTheme();
  return (
    <SimSubPageHeader className="Sim-sub-page-header" bgImage={bgImage}>
      <SimSubPageHBanner bgImage={bgImage}>
        <Container maxWidth="lg">
          {
            title && (
              <Typography
                variant={'h2'}
                fontWeight={700}
                textAlign={'center'}
                color={'common.white'}
                zIndex={0}
                mb={6}
                sx={{ ...theme.typography.underLine }}
              >
                { title }
              </Typography>
            )
          }
          {
            subtitle && (
              <Box>
                {subtitle}
              </Box>
            )
          }
        </Container>
      </SimSubPageHBanner>
    </SimSubPageHeader>
  );
};
SubPageHeader.propTypes = {
  title: PropTypes.string.isRequired
};

// ==================================|| SUB PAGE BODY ||================================== //

const SimSubPageBody = styled(Box)(
  ({ theme }) => `
`
);
const SubPageBody = ({ children, breadcrumbs }) => {
  const theme = useTheme();
  return (
    <SimSubPageBody className="Sim-sub-page-body">
      <PageBreadcrumbs breadcrumbs={breadcrumbs} />
      <Container
        maxWidth="lg"
        sx={{
          backgroundColor: theme => theme.palette.background.paper,
          [theme.breakpoints.up('lg')]: {
            p: 10
          },
          [theme.breakpoints.down('lg')]: {
            p: 3
          },
        }}
      >
        {children}
      </Container>
    </SimSubPageBody>
  );
};
SubPageBody.propTypes = {
  children: PropTypes.node.isRequired
};


// ==============================|| SUB PAGE ||============================== //

const SubPage = forwardRef(({ children, title = '', subtitle = '', breadcrumbs = [], bgImage = '', disableGutters = true, sx, ...other }, ref) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const padding = disableGutters ? {
    padding: 0
  } : {};
  return (
    <>
      <Helmet>
        <title>{`${title || MOCK_ACCOUNT({ t })?.title}`}</title>
      </Helmet>

      <SubPageContainer>
        <SubPageHeader
          bgImage={bgImage}
          title={title}
          subtitle={subtitle}
        />
        <SubPageBody breadcrumbs={breadcrumbs}>
          <Box >
            { children }
          </Box>
        </SubPageBody>
      </SubPageContainer>




      {
        false && (
          <Card
            ref={ref}
            sx={{
              pb: 12,
              backgroundColor: 'transparent',
              borderRadius: 0,
              '& .MuiCardHeader-root': {
                p: 0,
                position: 'relative',
                '&:before': {
                  content: '""',
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  left: '50%',
                  bottom: 0,
                  transform: 'translate(-50% , 0)',
                  zIndex: -1,
                  // backgroundColor: 'rgba(0, 43, 142, 0.82)',
                  // mixBlendMode: 'plus-darker',
                },
                '&:after': {
                  content: '""',
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  left: '50%',
                  bottom: 0,
                  transform: 'translate(-50% , 0)',
                  zIndex: -1,
                  backgroundImage: theme => theme.palette.gradients.blue1,
                },
                '& .MuiCardHeader-content': {
                  pt: 23,
                  pb: 11,
                  position: 'relative',
                  [theme.breakpoints.down('md')]: {
                    pt: 6,
                    pb: 6,
                  },
                  '&:after': {
                    content: '""',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    left: '50%',
                    bottom: 0,
                    transform: 'translate(-50% , 0)',
                    zIndex: -2,
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    backgroundColor: 'background.default',
                    backgroundImage: `url('${bgImage}')`,
                  },
                  '& .MuiCardHeader-title': {
                    ...theme.typography.h2,
                    ...theme.typography.underLine,
                    fontWeight: 700,
                    mb: 6,
                    display: 'block',
                    textAlign: 'center',
                    color: 'common.white',
                    position: 'relative',
                    zIndex: 0,
                  },
                  '& .MuiCardHeader-subheader': {
                    ...theme.typography.h6,
                    textAlign: 'center',
                    color: 'common.white',
                    position: 'relative',
                    zIndex: 0,
                  }
                },
              },
              '& .MuiCardContent-root': {
                ...padding,
              },
              ...sx
            }}
            {...other}
          >
            <CardHeader
              title={title}
              subheader={subtitle}
            />
            <CardContent>
              <SimBContainer>
                {/* Breadcrumbs start */}
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={
                    <Box
                      width={2}
                      height={2}
                      sx={{ backgroundColor: theme => theme.palette.error.main }}
                    />
                  }
                  sx={{ mb: 2.5 }}
                >
                  <Link component={RouterLink} fontSize={16} underline="hover" color="common.white" to="/honhai/app">
                    首頁
                  </Link>
                  {
                    _.map(breadcrumbs, (item, index) => {
                      return (
                        <Link
                          component={RouterLink}
                          key={index}
                          underline="hover"
                          color="common.white"
                          fontWeight={breadcrumbs?.length === (index + 1) ? 700 : 400}
                          fontSize={16}
                          to={item?.path}
                          sx={{ cursor: 'pointer' }}
                        >
                          { item?.title }
                        </Link>
                      )
                    })
                  }
                </Breadcrumbs>
                {/* Breadcrumbs end */}

                <Box p={10} sx={{ backgroundColor: theme => theme.palette.background.paper }}>
                  { children }
                </Box>
              </SimBContainer>
            </CardContent>
          </Card>
        )
      }
    </>
  )
});

SubPage.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  meta: PropTypes.node,
};

export default SubPage;
